import { useEffect, useRef, useState, type FormEvent } from "react";
import axios from "axios";
import { useQueryClient } from "@tanstack/react-query";
import styles from "./ProfileSettings.module.scss";
import Header from "../Header/header";
import AddTeamMemberModal, { type TeamMember } from "./AddTeamMemberModal";
import {
  asRecord, errorMessage, integerId, onboardingUrl, personalFrom, personalPayload,
  practiceFrom, practicePayload, recordFrom, requestHeaders, sitesFrom, sitesPayload,
  socialLinkFields, textValue, unwrap,
  type AddressInformation, type PersonalInformation, type PracticeInformation, type SiteInformation,
  type SocialLinks,
} from "./profileApi";

export type { PersonalInformation, PracticeInformation } from "./profileApi";
export type SettingsTab = "personal" | "practice" | "sites" | "team" | "general";
type EditableTab = "personal" | "practice" | "sites";
type SaveMessage = { error?: string; success?: string };

export interface GeneralSettings {
  language: string;
  timeZone: string;
  dateFormat: string;
  emailNotifications: boolean;
  weeklySummary: boolean;
  smsReminders: boolean;
  twoFactorAuthentication: boolean;
}

type IconName = "person" | "building" | "team" | "settings" | "camera" | "picture";

const samplePreferences: GeneralSettings = {
  language: "en-GB",
  timeZone: "Europe/London",
  dateFormat: "DD/MM/YYYY",
  emailNotifications: true,
  weeklySummary: true,
  smsReminders: false,
  twoFactorAuthentication: false,
};

const tabs: { id: SettingsTab; label: string; icon: IconName }[] = [
  { id: "personal", label: "Personal", icon: "person" },
  { id: "practice", label: "Practice", icon: "building" },
  { id: "sites", label: "Sites", icon: "building" },
  { id: "team", label: "Team members", icon: "team" },
  { id: "general", label: "General settings", icon: "settings" },
];

const languageOptions = [
  { value: "en-GB", label: "English (UK)" },
  { value: "en-US", label: "English (US)" },
];
const timeZoneOptions = ["Europe/London", "Asia/Colombo", "Australia/Sydney"];
const dateFormatOptions = ["DD/MM/YYYY", "MM/DD/YYYY", "YYYY-MM-DD"];

function Icon({ name, size = 16 }: { name: IconName; size?: number }) {
  const shared = {
    width: size,
    height: size,
    viewBox: "0 0 24 24",
    fill: "none",
    stroke: "currentColor",
    strokeWidth: 1.7,
    strokeLinecap: "round" as const,
    strokeLinejoin: "round" as const,
    "aria-hidden": true as const,
  };

  if (name === "person") return <svg {...shared}><circle cx="12" cy="7" r="3" /><path d="M5.5 20v-2a6.5 6.5 0 0 1 13 0v2" /></svg>;
  if (name === "building") return <svg {...shared}><rect x="5" y="3" width="11" height="18" rx="1" /><path d="M9 7h3M9 11h3M9 15h3M16 9h3v12M3 21h18" /></svg>;
  if (name === "team") return <svg {...shared}><circle cx="9" cy="8" r="3" /><path d="M2.5 20v-2a6.5 6.5 0 0 1 13 0v2M16 5.2a3 3 0 0 1 0 5.6M18 13a6 6 0 0 1 3.5 5.5V20" /></svg>;
  if (name === "settings") return <svg {...shared}><path d="M9.8 3.3h4.4l.5 2.2 1.5.9 2.2-.7 2.2 3.8-1.7 1.5v1.8l1.7 1.5-2.2 3.8-2.2-.7-1.5.9-.5 2.2H9.8l-.5-2.2-1.5-.9-2.2.7-2.2-3.8 1.7-1.5V11L3.4 9.5l2.2-3.8 2.2.7 1.5-.9z" /><circle cx="12" cy="12" r="3" /></svg>;
  if (name === "camera") return <svg {...shared}><path d="M4 7h3l1.4-2h7.2L17 7h3v12H4z" /><circle cx="12" cy="13" r="3.2" /></svg>;
  return <svg {...shared}><rect x="3" y="3" width="18" height="18" rx="2" /><circle cx="8.5" cy="8" r="1.5" /><path d="m4 18 5-5 3 3 4-5 4 5" /></svg>;
}

function Field({
  label,
  value,
  onChange,
  type = "text",
  placeholder,
  autoComplete,
  required = false,
  readOnly = false,
  pattern,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  type?: string;
  placeholder?: string;
  autoComplete?: string;
  required?: boolean;
  readOnly?: boolean;
  pattern?: string;
}) {
  return (
    <label className={styles.field}>
      <span>{label}{required && <span className={styles.requiredMark}> *</span>}</span>
      <input
        type={type}
        value={value}
        onChange={(event) => onChange(event.target.value)}
        placeholder={placeholder}
        autoComplete={autoComplete}
        required={required}
        readOnly={readOnly}
        pattern={pattern}
      />
    </label>
  );
}

function AddressFields({ value, onChange, practice = false }: {
  value: AddressInformation;
  onChange: (key: Exclude<keyof AddressInformation, "socialLinks">, value: string) => void;
  practice?: boolean;
}) {
  return (
    <div className={styles.formGrid}>
      <Field label={practice ? "Practice name" : "Site name"} required value={value.name} onChange={(v) => onChange("name", v)} autoComplete="organization" />
      <Field label="Phone" type="tel" value={value.phone} onChange={(v) => onChange("phone", v)} autoComplete="work tel" />
      <Field label={practice ? "Practice email" : "Site email"} type="email" required={practice} value={value.email} onChange={(v) => onChange("email", v)} />
      <Field label="Address line 1" required value={value.addressLine1} onChange={(v) => onChange("addressLine1", v)} autoComplete="address-line1" />
      <Field label="Address line 2" value={value.addressLine2} onChange={(v) => onChange("addressLine2", v)} autoComplete="address-line2" />
      <Field label="Town" required value={value.town} onChange={(v) => onChange("town", v)} autoComplete="address-level2" />
      <Field label="Postcode" required value={value.postcode} onChange={(v) => onChange("postcode", v)} autoComplete="postal-code" />
    </div>
  );
}

function SocialLinkFields({ value, onChange }: { value: SocialLinks; onChange: (value: SocialLinks) => void }) {
  return (
    <details className={styles.socialLinks} onInvalidCapture={(event) => { event.currentTarget.open = true; }}>
      <summary>Social links</summary>
      <div className={styles.formGrid}>
        {socialLinkFields.map(([key, label]) => (
          <Field key={key} label={label} type={key === "email" ? "email" : "text"} value={value[key]} onChange={(v) => onChange({ ...value, [key]: v })} />
        ))}
      </div>
    </details>
  );
}

function FormMessage({ message }: { message: SaveMessage }) {
  return <>
    {message.error && <p className={`${styles.notice} ${styles.error}`} role="alert">{message.error}</p>}
    {message.success && <p className={`${styles.notice} ${styles.success}`} role="status">{message.success}</p>}
  </>;
}

export default function ProfileSettings() {
  const [showAddMember, setShowAddMember] = useState(false);
  const [teamMembers, setTeamMembers] = useState<TeamMember[]>([]);
  const [memberStatus, setMemberStatus] = useState("");
  const [activeTab, setActiveTab] = useState<SettingsTab>("personal");
  const [personal, setPersonal] = useState<PersonalInformation>(() => personalFrom());
  const [practiceForm, setPracticeForm] = useState<PracticeInformation>(() => practiceFrom());
  const [sites, setSites] = useState<SiteInformation[]>([]);
  const [generalForm, setGeneralForm] = useState<GeneralSettings>(samplePreferences);
  const [loading, setLoading] = useState(true);
  const [loadAttempt, setLoadAttempt] = useState(0);
  const [loadError, setLoadError] = useState("");
  const [clinicianId, setClinicianId] = useState<number | null>(null);
  const [practiceId, setPracticeId] = useState<number | null>(null);
  const [savingTab, setSavingTab] = useState<EditableTab | null>(null);
  const [messages, setMessages] = useState<Record<EditableTab, SaveMessage>>({ personal: {}, practice: {}, sites: {} });
  const submitting = useRef(false);
  const previousMobile = useRef("");
  const previousPaymentPlanId = useRef("");
  const queryClient = useQueryClient();

  useEffect(() => {
    let cancelled = false;
    async function loadProfile() {
      setLoading(true);
      setLoadError("");
      setMessages({ personal: {}, practice: {}, sites: {} });
      setClinicianId(null);
      setPracticeId(null);
      try {
        const config = { headers: requestHeaders() };
        const [userResult, practiceResult, clinicianResult] = await Promise.allSettled([
          axios.get("/user", config),
          axios.get(onboardingUrl("get_practice_data"), config),
          axios.get(onboardingUrl("get_practitioner_data"), config),
        ]);
        if (cancelled) return;
        const userResponse = userResult.status === "fulfilled" ? unwrap(userResult.value.data) : {};
        const user = asRecord(Array.isArray(userResponse) ? userResponse[1] ?? userResponse[0] : userResponse);
        const clinician = clinicianResult.status === "fulfilled" ? recordFrom(clinicianResult.value.data, "practitioner") : {};
        let practice = practiceResult.status === "fulfilled" ? recordFrom(practiceResult.value.data, "practice") : {};
        const id = integerId(practice.id ?? practice.practice_id ?? user.medicube_practice_id);
        let practiceLoaded = practiceResult.status === "fulfilled" && Object.keys(practice).length > 0;
        // The full practice record can include structured addresses and its existing sites.
        if (id && (!Array.isArray(unwrap(practice.sites)) || !practice.address_line_1)) {
          try {
            const response = await axios.get(`/practice/${id}`, config);
            practice = { ...practice, ...recordFrom(response.data, "practice") };
            practiceLoaded = Object.keys(practice).length > 0;
          } catch (error) {
            if (cancelled) return;
            setMessages((current) => ({ ...current, sites: { error: errorMessage(error, "load sites") } }));
          }
        }
        if (cancelled) return;
        const nextPersonal = personalFrom(clinician, user);
        const nextPractice = practiceFrom(practice);
        const currentClinicianId = clinicianResult.status === "fulfilled" && Object.keys(clinician).length > 0
          ? integerId(clinician.id ?? clinician.clinician_id ?? user.medicube_clinician_id ?? user.clinician_id) : null;
        setPersonal(nextPersonal);
        previousMobile.current = nextPersonal.mobile;
        setPracticeForm(nextPractice);
        previousPaymentPlanId.current = nextPractice.paymentPlanId;
        setClinicianId(currentClinicianId);
        setPracticeId(practiceLoaded ? id : null);
        setSites(sitesFrom(practice));
        if (!currentClinicianId) setMessages((current) => ({ ...current, personal: { error: clinicianResult.status === "rejected"
          ? errorMessage(clinicianResult.reason, "load personal details") : "The clinician ID is missing. Please reload your profile." } }));
        if (!id || !practiceLoaded) setMessages((current) => ({ ...current, practice: { error: practiceResult.status === "rejected"
          ? errorMessage(practiceResult.reason, "load practice details") : "The practice ID is missing. Please reload your profile." } }));
        if (userResult.status === "rejected") setLoadError(errorMessage(userResult.reason, "load your account"));
      } catch (error) {
        if (!cancelled) setLoadError(errorMessage(error, "load your profile"));
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    void loadProfile();
    return () => { cancelled = true; };
  }, [loadAttempt]);

  function clearMessage(tab: EditableTab) {
    setMessages((current) => ({ ...current, [tab]: {} }));
  }

  function updatePersonal<K extends keyof PersonalInformation>(key: K, value: PersonalInformation[K]) {
    setPersonal((current) => ({ ...current, [key]: value }));
    clearMessage("personal");
  }

  function updatePractice<K extends keyof PracticeInformation>(key: K, value: PracticeInformation[K]) {
    setPracticeForm((current) => ({ ...current, [key]: value }));
    clearMessage("practice");
  }

  function updateGeneral<K extends keyof GeneralSettings>(key: K, value: GeneralSettings[K]) {
    setGeneralForm((current) => ({ ...current, [key]: value }));
  }

  function updateSite(index: number, values: Partial<SiteInformation>) {
    setSites((current) => current.map((site, i) => i === index ? { ...site, ...values } : site));
    clearMessage("sites");
  }

  async function saveSection(event: FormEvent<HTMLFormElement>, tab: EditableTab) {
    event.preventDefault();
    if (submitting.current || loading) return;
    clearMessage(tab);
    submitting.current = true;
    setSavingTab(tab);
    try {
      const config = { headers: requestHeaders() };
      if (tab === "personal") {
        if (!clinicianId) throw new Error("The clinician ID is missing. Please reload your profile.");
        const body = personalPayload(personal, previousMobile.current);
        const response = await axios.post(onboardingUrl(`update_non_dentally_clinician/${clinicianId}`), body, config);
        const record = recordFrom(response.data, "clinician");
        const saved = personalFrom({ ...body, ...record, email: personal.email, role: personal.role, image_url: personal.avatarUrl });
        setPersonal(saved);
        previousMobile.current = saved.mobile;
        void queryClient.invalidateQueries({ queryKey: ["user"] });
      } else if (tab === "practice") {
        if (!practiceId) throw new Error("The practice ID is missing. Please reload your profile.");
        const body = practicePayload(practiceForm, previousPaymentPlanId.current);
        const response = await axios.post(onboardingUrl(`update_non_dentally_practice/${practiceId}`), body, config);
        const saved = practiceFrom({ ...body, ...recordFrom(response.data, "practice") });
        setPracticeForm(saved);
        previousPaymentPlanId.current = saved.paymentPlanId;
        void queryClient.invalidateQueries({ queryKey: ["practice", practiceId] });
      } else {
        const body = sitesPayload(sites);
        const response = await axios.post(onboardingUrl("update_non_dentally_sites"), body, config);
        const raw = unwrap(response.data);
        const returned = unwrap(asRecord(raw).sites ?? raw);
        // Match by ID because the server can return sites in a different order.
        const records = Array.isArray(returned) ? returned.map(asRecord) : [];
        setSites(body.map((site) => sitesFrom({ sites: [{ ...site, ...records.find((record) => textValue(record.id) === site.id) }] })[0]));
        if (practiceId) void queryClient.invalidateQueries({ queryKey: ["practice", practiceId] });
      }
      setMessages((current) => ({ ...current, [tab]: { success: tab === "personal" ? "Personal details saved." : tab === "practice" ? "Practice details saved." : "Sites saved." } }));
    } catch (error) {
      setMessages((current) => ({ ...current, [tab]: { error: errorMessage(error, `save ${tab === "personal" ? "personal details" : tab === "practice" ? "practice details" : "sites"}`) } }));
    } finally {
      submitting.current = false;
      setSavingTab(null);
    }
  }

  function switchTab(tab: SettingsTab) {
    setActiveTab(tab);
  }

  const fullName = personal.name;
  const initials = fullName.trim().split(/\s+/).slice(0, 2).map((part) => part.charAt(0)).join("").toUpperCase() || "?";

  return (
    <>
      <Header />
      <main className={styles.page}>
        <div className={styles.container}>
          <header className={styles.profileCard}>
            <div className={styles.avatarWrap}>
              <div className={styles.avatar}>
                {personal.avatarUrl ? <img src={personal.avatarUrl} alt="Profile" /> : initials}
              </div>
            </div>
            <div className={styles.profileSummary}>
              <h1>{fullName || "My profile"}</h1>
              <p>{personal.email}{personal.email && practiceForm.name ? " · " : ""}{practiceForm.name}</p>
              {personal.role && <span className={styles.roleBadge}>{personal.role}</span>}
            </div>
          </header>
          {loading && <p className={styles.notice} role="status">Loading profile details...</p>}
          {loadError && <p className={`${styles.notice} ${styles.error}`} role="alert">{loadError}</p>}
          {!loading && <div className={styles.reloadRow}><button className={styles.outlineButton} type="button" disabled={savingTab !== null} onClick={() => setLoadAttempt((current) => current + 1)}>Reload profile</button></div>}
  
          <div className={styles.tabBar} role="tablist" aria-label="Profile settings">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                id={`profile-tab-${tab.id}`}
                type="button"
                role="tab"
                aria-selected={activeTab === tab.id}
                aria-controls={activeTab === tab.id ? `profile-panel-${tab.id}` : undefined}
                tabIndex={activeTab === tab.id ? 0 : -1}
                className={`${styles.tab} ${activeTab === tab.id ? styles.activeTab : ""}`}
                onClick={() => switchTab(tab.id)}
                onKeyDown={(event) => {
                  const direction = event.key === "ArrowRight" ? 1 : event.key === "ArrowLeft" ? -1 : 0;
                  if (!direction) return;
                  event.preventDefault();
                  const index = tabs.findIndex((item) => item.id === activeTab);
                  const next = tabs[(index + direction + tabs.length) % tabs.length].id;
                  switchTab(next);
                  document.getElementById(`profile-tab-${next}`)?.focus();
                }}
              ><Icon name={tab.icon} size={15} />{tab.label}</button>
            ))}
          </div>
  
          <section
            className={styles.panel}
            id={`profile-panel-${activeTab}`}
            role="tabpanel"
            aria-labelledby={`profile-tab-${activeTab}`}
            tabIndex={0}
          >
            {activeTab === "personal" && (
              <>
                <div className={styles.sectionHeader}>
                  <h2>Personal information</h2>
                  <p>Your private details. Only you and admins can see these.</p>
                </div>
                <form aria-label="Personal information" onSubmit={(event) => void saveSection(event, "personal")} aria-busy={savingTab === "personal"}>
                  <fieldset className={styles.formFields} disabled={loading || savingTab !== null || !clinicianId}>
                    <div className={styles.formGrid}>
                      <Field label="Full name" value={personal.name} onChange={(v) => updatePersonal("name", v)} autoComplete="name" />
                      <Field label="Email" type="email" readOnly value={personal.email} onChange={() => {}} autoComplete="email" />
                      <Field label="Mobile" type="tel" value={personal.mobile} onChange={(v) => updatePersonal("mobile", v)} autoComplete="tel" />
                      <Field label="Mobile phone country" value={personal.mobileCountry} placeholder="GB" onChange={(v) => updatePersonal("mobileCountry", v)} />
                      <Field label="GDC number" value={personal.gdcNumber} onChange={(v) => updatePersonal("gdcNumber", v)} />
                      <Field label="NHS number" value={personal.nhsNumber} onChange={(v) => updatePersonal("nhsNumber", v)} />
                    </div>
                    <div className={styles.actions}><button className={styles.primaryButton} type="submit">{savingTab === "personal" ? "Saving..." : "Save changes"}</button></div>
                  </fieldset>
                  <FormMessage message={messages.personal} />
                </form>
              </>
            )}
  
            {activeTab === "practice" && (
              <>
                <div className={styles.sectionHeader}>
                  <h2>Practice information</h2>
                  <p>Shown on consent forms and patient emails.</p>
                </div>
                <form aria-label="Practice information" onSubmit={(event) => void saveSection(event, "practice")} aria-busy={savingTab === "practice"}>
                  <fieldset className={styles.formFields} disabled={loading || savingTab !== null || !practiceId}>
                    <AddressFields practice value={practiceForm} onChange={(key, value) => updatePractice(key, value)} />
                    <div className={styles.optionalFields}>
                      <Field label="NHS payment plan ID" value={practiceForm.paymentPlanId} pattern="[0-9]+" onChange={(v) => updatePractice("paymentPlanId", v)} />
                    </div>
                    <SocialLinkFields value={practiceForm.socialLinks} onChange={(links) => updatePractice("socialLinks", links)} />
                    <div className={styles.actions}><button className={styles.primaryButton} type="submit">{savingTab === "practice" ? "Saving..." : "Save practice"}</button></div>
                  </fieldset>
                  <FormMessage message={messages.practice} />
                </form>
              </>
            )}

            {activeTab === "sites" && (
              <>
                <div className={styles.sectionHeader}>
                  <h2>Site information</h2>
                  <p>Update the locations belonging to your practice.</p>
                </div>
                <form aria-label="Site information" onSubmit={(event) => void saveSection(event, "sites")} aria-busy={savingTab === "sites"}>
                  <fieldset className={styles.formFields} disabled={loading || savingTab !== null}>
                    {sites.length === 0 && !loading && <p className={styles.emptyTeam}>No sites are available for this practice.</p>}
                    {sites.map((site, index) => (
                      <fieldset key={index} className={styles.siteCard}>
                        <legend>Site {index + 1}</legend>
                        <div className={`${styles.formGrid} ${styles.siteIdentity}`}>
                          <Field label="Site ID" readOnly value={site.id} onChange={() => {}} />
                          <Field label="Display name" required value={site.displayName} onChange={(v) => updateSite(index, { displayName: v })} />
                        </div>
                        <AddressFields value={site} onChange={(key, value) => updateSite(index, { [key]: value })} />
                        <SocialLinkFields value={site.socialLinks} onChange={(links) => updateSite(index, { socialLinks: links })} />
                      </fieldset>
                    ))}
                    <div className={styles.actions}><button className={styles.primaryButton} type="submit" disabled={!sites.length}>{savingTab === "sites" ? "Saving..." : "Save sites"}</button></div>
                  </fieldset>
                  <FormMessage message={messages.sites} />
                </form>
              </>
            )}
  
            {activeTab === "team" && (
              <>
                <div className={`${styles.sectionHeader} ${styles.teamHeader}`}>
                  <div>
                    <h2>Team members</h2>
                    <p>People with access to your practice.</p>
                  </div>
                  <button type="button" className={`${styles.primaryButton} ${styles.addMemberButton}`} onClick={() => setShowAddMember(true)}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true"><path d="M12 5v14M5 12h14" /></svg>
                    Add member
                  </button>
                </div>
                <p className={styles.memberStatus} role="status">{memberStatus}</p>
                {teamMembers.length === 0 ? (
                  <p className={styles.emptyTeam}>No team members to show.</p>
                ) : (
                  <ul className={styles.memberList} aria-label="Team members">
                    {teamMembers.map((member) => (
                      <li key={member.email.toLowerCase()} className={styles.memberRow}>
                        <span className={styles.memberAvatar} aria-hidden="true">{member.email.slice(0, 2).toUpperCase()}</span>
                        <div className={styles.memberDetails}><strong>{member.email}</strong></div>
                        <div className={styles.memberBadges}>
                          <span className={styles.memberRole}>{member.role}</span>
                          <span className={styles.memberRole}>{member.userGroup === "admin" ? "Admin" : "User"}</span>
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
              </>
            )}
  
            {activeTab === "general" && (
              <>
                <div className={styles.sectionHeader}>
                  <h2>General settings</h2>
                  <p>Preferences for your account.</p>
                </div>
                <form onSubmit={(event) => event.preventDefault()}>
                  <div className={styles.selectGrid}>
                    <label className={styles.field}><span>Language</span><select value={generalForm.language} onChange={(e) => updateGeneral("language", e.target.value)}>{!languageOptions.some((item) => item.value === generalForm.language) && <option value={generalForm.language}>{generalForm.language}</option>}{languageOptions.map((item) => <option key={item.value} value={item.value}>{item.label}</option>)}</select></label>
                    <label className={styles.field}><span>Time zone</span><select value={generalForm.timeZone} onChange={(e) => updateGeneral("timeZone", e.target.value)}>{!timeZoneOptions.includes(generalForm.timeZone) && <option value={generalForm.timeZone}>{generalForm.timeZone}</option>}{timeZoneOptions.map((item) => <option key={item} value={item}>{item}</option>)}</select></label>
                    <label className={styles.field}><span>Date format</span><select value={generalForm.dateFormat} onChange={(e) => updateGeneral("dateFormat", e.target.value)}>{!dateFormatOptions.includes(generalForm.dateFormat) && <option value={generalForm.dateFormat}>{generalForm.dateFormat}</option>}{dateFormatOptions.map((item) => <option key={item} value={item}>{item}</option>)}</select></label>
                  </div>
                  <div className={styles.switchList}>
                    {([
                      ["emailNotifications", "Email notifications", "Get emailed when a patient signs a consent form"],
                      ["weeklySummary", "Weekly summary", "Receive a weekly report of consent activity"],
                      ["smsReminders", "SMS reminders", "Send patients SMS reminders for unsigned forms"],
                      ["twoFactorAuthentication", "Two-factor authentication", "Add an extra layer of security to your account"],
                    ] as const).map(([key, label, description]) => (
                      <label className={styles.switchRow} key={key}>
                        <span className={styles.switchText}><strong>{label}</strong><span>{description}</span></span>
                        <input type="checkbox" className={styles.switchInput} checked={generalForm[key]} onChange={(e) => updateGeneral(key, e.target.checked)} />
                        <span className={styles.switchVisual} aria-hidden="true" />
                      </label>
                    ))}
                  </div>
                  <div className={`${styles.actions} ${styles.generalActions}`}>
                    <button className={styles.deleteButton} type="button">Delete account</button>
                    <button className={styles.primaryButton} type="submit">Save settings</button>
                  </div>
                </form>
              </>
            )}
          </section>
        </div>
        {showAddMember && (
          <AddTeamMemberModal
            members={teamMembers}
            onClose={() => setShowAddMember(false)}
            onAdd={(member) => {
              setTeamMembers((current) => [...current, member]);
              setMemberStatus(`${member.email} added to the team.`);
              setShowAddMember(false);
            }}
          />
        )}
      </main>
    </>
  );
}
